import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, Share2, Copy, ImageDown } from "lucide-react";
import { formatMoney } from "@/lib/currency";
import SnapshotCard from "@/components/monthly-snapshot/SnapshotCard";
import { useMonthlySnapshotExport } from "@/hooks/useMonthlySnapshotExport";

/**
 * MonthlySnapshotModal — opens a share sheet + PNG for the current month.
 *
 * Slim orchestrator (iter-45 refactor): the off-screen 1080×1080 card
 * lives in `components/monthly-snapshot/SnapshotCard.jsx` and the
 * generatePng/download/share/copy flow lives in
 * `hooks/useMonthlySnapshotExport.js`.
 *
 * Props:
 *   open           — controls visibility
 *   onClose        — callback
 *   summary        — dashboard/summary payload (net_worth, cashflow_this_month, etc.)
 *   streakCount    — number of consecutive positive months
 *   topInsight     — optional { title, monthly_savings } from optimizer_insights
 *   currency       — user currency code
 *   userName       — display name for the caption
 */
export default function MonthlySnapshotModal({
  open, onClose, summary, streakCount = 0, topInsight, currency = "USD", userName,
}) {
  const { t } = useTranslation();
  const { cardRef, exporting, downloadPng, shareImage, copyOnly } = useMonthlySnapshotExport();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !summary) return null;

  const now = new Date();
  const monthLabel = now.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const monthShort = now.toLocaleDateString(undefined, { month: "long" });
  const netWorth = Number(summary.net_worth) || 0;
  const cashflow = Number(summary.cashflow_this_month) || 0;
  const cashflowPos = cashflow >= 0;

  const firstName = (userName || "").split(" ")[0] || "";
  const streakSuffix = streakCount
    ? t("snapshot_modal.streak_suffix", { count: streakCount })
    : "";
  const shareText = t(
    cashflowPos ? "snapshot_modal.share_text_saved" : "snapshot_modal.share_text_spent",
    {
      month: monthShort,
      name_suffix: firstName ? " · " + firstName : "",
      networth: formatMoney(netWorth, currency),
      amount: formatMoney(Math.abs(cashflow), currency),
      streak_suffix: streakSuffix,
    },
  );
  const filenameBase = `pfo-snapshot-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
        style={{ background: "rgba(15, 20, 25, 0.72)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="monthly-snapshot-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-lift animate-fadeup"
          style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #FBF5EB 100%)",
            border: "1px solid rgba(224,158,80,0.35)",
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-accent)] transition-colors"
            data-testid="monthly-snapshot-close-btn"
            aria-label={t("snapshot_modal.close")}
          >
            <X size={16} />
          </button>

          <div className="p-7 text-center">
            <div className="label-eyebrow" style={{ color: "#A16A25" }}>{t("snapshot_modal.eyebrow")}</div>
            <h2
              className="text-2xl md:text-3xl font-medium tracking-tight leading-tight mt-2"
              style={{ fontFamily: "var(--font-heading)" }}
              data-testid="monthly-snapshot-title"
            >
              {t("snapshot_modal.title", { month: monthShort })}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
              {t("snapshot_modal.body_base")}
              {streakCount ? t("snapshot_modal.body_streak") : ""}
              {topInsight ? t("snapshot_modal.body_top_insight", { month: monthShort }) : ""}
              {t("snapshot_modal.body_tail")}
            </p>

            <div className="mt-6 flex items-center gap-2 justify-center flex-wrap">
              <button
                onClick={() => shareImage(filenameBase, shareText, t("snapshot_modal.share_title_my_month"))}
                className="btn-primary"
                disabled={exporting}
                data-testid="monthly-snapshot-share-btn"
              >
                <Share2 size={14} />
                {exporting ? t("snapshot_modal.preparing") : t("snapshot_modal.share_image")}
              </button>
              <button
                onClick={() => downloadPng(filenameBase)}
                className="btn-ghost"
                title={t("snapshot_modal.save_png")}
                disabled={exporting}
                data-testid="monthly-snapshot-download-btn"
              >
                <ImageDown size={14} />
              </button>
              <button
                onClick={() => copyOnly(shareText)}
                className="btn-ghost"
                title={t("snapshot_modal.copy_caption")}
                data-testid="monthly-snapshot-copy-btn"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Off-screen 1080×1080 share card */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: "-99999px",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <SnapshotCard
          ref={cardRef}
          monthLabel={monthLabel}
          netWorth={netWorth}
          cashflow={cashflow}
          streakCount={streakCount}
          topInsight={topInsight}
          currency={currency}
          userName={userName}
        />
      </div>
    </>,
    document.body,
  );
}
